import React from 'react';
import type { ReactNode } from 'react';
import type { UseFormRegisterReturn } from 'react-hook-form';

export interface InputProps
    extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
    label?: string;
    registration?: UseFormRegisterReturn;
    error?: string;
    type?: React.HTMLInputTypeAttribute;
    rightElement?: ReactNode;
    leftElement?: ReactNode;
    required?: boolean;
    isDisabled?: boolean;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
    ({ isDisabled = false, label, registration, error, id, type = 'text', className = '', rightElement, leftElement, required, ...rest }, ref) => {
        const inputId = id ?? registration?.name ?? label?.toLowerCase().replace(/\s+/g, '-');

        return (
            <div className="mb-[10px] 2xl:mb-[15px] w-full">
                {label && (
                    <label htmlFor={inputId} className="text-[12px] 2xl:text-[14px]  font-medium text-[var(--secondary-color)] mb-1">
                        {label}
                        {required && <span className="text-[var(--semantic-error-color)] ml-0.5">*</span>}
                    </label>
                )}
                <div className="relative flex items-center">
                    <input
                        id={inputId}
                        disabled={isDisabled}
                        type={type}
                        className={[
                            'w-full rounded-md border px-3 py-[6px] 2xl:py-[9px] text-[12px] 2xl:text-[14px] text-[var(--secondary-color)] bg-[var(--primary-canvas-color)]',
                            'placeholder:text-[var(--disabled-text-color)] outline-none',
                            'transition duration-150',
                            rightElement ? 'pr-8 2xl:pr-9' : '',
                            leftElement ? 'pl-8 2xl:pl-9' : '',
                            error
                                ? 'border-[var(--semantic-error-color)] focus:border-[(--semantic-error-color)]'
                                : 'border-[var(--neutral-color)] focus:border-[var(--primary-color)]',
                            'disabled:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-70',
                            className,
                        ].filter(Boolean).join(' ')}
                        {...registration}
                        ref={(node) => {
                            if (registration?.ref) registration.ref(node);
                            if (typeof ref === 'function') ref(node);
                            else if (ref) (ref as React.MutableRefObject<HTMLInputElement | null>).current = node;
                        }}
                        {...rest}
                        onKeyDown={(e) => {
                            if (type === 'number') {
                                if (e.key === '-' || e.key === 'e' || e.key === 'E') {
                                    e.preventDefault();
                                }
                            }
                        
                            if (e.key === ' ' && (e.currentTarget.selectionStart ?? 0) === 0) {
                                e.preventDefault();
                            }
                        
                            rest.onKeyDown?.(e);
                        }}
                    />

                    {rightElement && (
                        <span className="absolute right-2.5 flex items-center cursor-pointer">
                            {rightElement}
                        </span>
                    )}
                    {leftElement && (
                        <span className="absolute left-2.5 mt-[-2px] flex items-center cursor-pointer">
                            {leftElement}
                        </span>
                    )}
                </div>

                {error && (
                    <span className="text-[11px] text-[var(--semantic-error-color)]" role="alert">
                        {error}
                    </span>
                )}
            </div>
        );
    },
);

Input.displayName = 'Input';

export default Input;